// Central localStorage helpers for Projection Lab data safety.
//
// Key scheme:
//   projlab-<abbrev>                  — a team's saved projection (the real data)
//   projlab-snapshot-<timestamp>      — rolling auto-snapshot taken on Lab entry
//   projlab-<abbrev>-quarantine-<ts>  — a save we couldn't safely parse, set aside
//
// The export/import envelope ALSO carries the Lab's two other stores — the
// per-player rate edits and the per-team depth overrides, both owned by
// depthOverrides.js — under `labStores`. They are not projlab-* keys, but they
// are two thirds of what a user would lose, so a backup that omits them is not
// one. See buildProjlabExport.
//
// PROJLAB_SCHEMA_VERSION is the data-safety envelope version (export/import +
// quarantine checks). It is DISTINCT from TeamEditor's per-team content `version`
// (SAVE_VERSION), which drives content migrations.

import {
  loadPlayerEdits, savePlayerEdits, clearAllPlayerEdits,
  readAllDepthOverrides, restoreAllDepthOverrides,
} from "./depthOverrides";

export const PROJLAB_SCHEMA_VERSION = 1;
export const PROJLAB_PREFIX = "projlab-";

// True for a user team-projection key (projlab-<abbrev>) — excludes the kit's own
// internal keys (snapshots, quarantines, backups) so we never round-trip those.
export function isProjlabTeamKey(key) {
  return typeof key === "string"
    && key.startsWith(PROJLAB_PREFIX)
    && !key.includes("-snapshot-")
    && !key.includes("-quarantine-")
    && !key.includes("-backup-");
}

// Every team-projection key currently in localStorage.
export function listProjlabTeamKeys() {
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (isProjlabTeamKey(k)) keys.push(k);
    }
  } catch { /* localStorage unavailable */ }
  return keys;
}

// Build the export envelope: { schemaVersion, exportedAt, keys: { <key>: value } }.
// Values are parsed objects when possible; a corrupt value is preserved as its
// raw string so a backup never silently drops data.
export function buildProjlabExport() {
  const keys = {};
  for (const k of listProjlabTeamKeys()) {
    let raw;
    try { raw = localStorage.getItem(k); } catch { continue; }
    if (raw == null) continue;
    try { keys[k] = JSON.parse(raw); }
    catch { keys[k] = raw; }
  }
  return {
    schemaVersion: PROJLAB_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    keys,
    // 🔴 ALL THREE STORES OR IT IS NOT A BACKUP. `keys` alone leaves the user's
    // per-player rate edits and depth overrides behind, and SYNC TO HOUSE clears
    // all three — so an envelope without these could not undo it.
    labStores: {
      playerEdits: loadPlayerEdits(),
      depthOverrides: readAllDepthOverrides(),
    },
  };
}

const SNAPSHOT_PREFIX = `${PROJLAB_PREFIX}snapshot-`;
const SNAPSHOTS_TO_KEEP = 3;

// Write a rolling snapshot of every team key (called on Lab entry), then prune to
// the last SNAPSHOTS_TO_KEEP. Stores exact raw bytes so a snapshot is a faithful
// point-in-time copy. No-op (and never throws) if there's nothing to snapshot.
export function writeProjlabSnapshot() {
  try {
    const keys = {};
    for (const k of listProjlabTeamKeys()) {
      const raw = localStorage.getItem(k);
      if (raw != null) keys[k] = raw;
    }
    if (Object.keys(keys).length === 0) return;
    const snap = { schemaVersion: PROJLAB_SCHEMA_VERSION, takenAt: new Date().toISOString(), keys };
    localStorage.setItem(`${SNAPSHOT_PREFIX}${Date.now()}`, JSON.stringify(snap));
    pruneProjlabSnapshots();
  } catch (e) {
    console.warn("[projlab] auto-snapshot failed:", e);
  }
}

function pruneProjlabSnapshots() {
  try {
    const snaps = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(SNAPSHOT_PREFIX)) snaps.push(k);
    }
    // keys end in -<timestamp>; oldest first, drop everything beyond the keep count.
    snaps.sort((a, b) => Number(a.slice(SNAPSHOT_PREFIX.length)) - Number(b.slice(SNAPSHOT_PREFIX.length)));
    while (snaps.length > SNAPSHOTS_TO_KEEP) {
      localStorage.removeItem(snaps.shift());
    }
  } catch { /* best-effort prune */ }
}

// Move a key aside instead of losing it: copy its raw value to
// projlab-<...>-quarantine-<timestamp>, remove the original, warn. Used when a
// save can't be safely parsed — preserves the bytes for manual recovery while
// letting the caller start that team fresh. NEVER deletes without preserving.
export function quarantineProjlabKey(key, reason) {
  let raw = null;
  try { raw = localStorage.getItem(key); } catch { return; }
  try {
    if (raw != null) localStorage.setItem(`${key}-quarantine-${Date.now()}`, raw);
    localStorage.removeItem(key);
    console.warn(`[projlab] quarantined "${key}" (${reason}) — preserved a copy, starting that team fresh.`);
  } catch (e) {
    console.warn(`[projlab] quarantine failed for "${key}":`, e);
  }
}

// Safe read of a projlab-<abbrev> team save. On unparseable JSON or an
// incompatible (future) schemaVersion, it NEVER deletes/overwrites blindly — it
// quarantines the key and returns null so the caller starts that team fresh.
// Legacy saves without a schemaVersion are returned as-is (handled downstream by
// TeamEditor's content `version` migration).
export function safeReadProjlab(abbrev) {
  const key = `${PROJLAB_PREFIX}${abbrev}`;
  let raw;
  try { raw = localStorage.getItem(key); } catch { return null; }
  if (raw == null) return null;
  let parsed;
  try { parsed = JSON.parse(raw); }
  catch { quarantineProjlabKey(key, "unparseable JSON"); return null; }
  if (parsed && typeof parsed === "object" && Number(parsed.schemaVersion) > PROJLAB_SCHEMA_VERSION) {
    quarantineProjlabKey(key, `unknown schemaVersion ${parsed.schemaVersion}`);
    return null;
  }
  return parsed;
}

// Delete a team's save outright: projlab-<abbrev>, gone. Returns true when the
// key existed, false when it did not (or when storage threw — a delete that
// could not happen is not a delete). Deliberately does NOT quarantine the way
// quarantineProjlabKey does: this is the user asking to fall back to the house
// projections, and the house set IS the recovery.
// 🔴 Do not call this directly from UI — go through projectionSource.clearUserTeam,
// which pairs the removal with the cache invalidation it requires.
export function removeProjlabTeam(abbrev) {
  const key = `${PROJLAB_PREFIX}${abbrev}`;
  try {
    const existed = localStorage.getItem(key) != null;
    localStorage.removeItem(key);
    return existed;
  } catch {
    return false;
  }
}

// Apply an export envelope produced by buildProjlabExport(). Validates shape,
// writes each team key back INDEPENDENTLY — a single malformed entry is skipped
// and reported, never aborting the others (no partial wipe). Throws only when the
// whole file is invalid/too-new, in which case nothing is written at all.
// Returns { imported, skipped: [{ key, reason }], schemaVersion }.
export function importProjlabBackup(parsed) {
  if (!parsed || typeof parsed !== "object" || !parsed.keys || typeof parsed.keys !== "object") {
    throw new Error("Not a valid projections backup (missing schemaVersion / keys).");
  }
  if (Number(parsed.schemaVersion) > PROJLAB_SCHEMA_VERSION) {
    throw new Error(`Backup schemaVersion ${parsed.schemaVersion} is newer than this app supports (${PROJLAB_SCHEMA_VERSION}).`);
  }
  const report = {
    imported: 0, skipped: [], schemaVersion: parsed.schemaVersion ?? null,
    playerEdits: 0, depthTeams: 0,
  };
  for (const [key, value] of Object.entries(parsed.keys)) {
    if (!isProjlabTeamKey(key)) { report.skipped.push({ key, reason: "not a projlab team key" }); continue; }
    try {
      // value is normally a parsed object; a corrupt-passthrough export stores it
      // as a raw string. Either way, store a JSON string and verify it parses to
      // an object first — never overwrite a good key with garbage.
      const toStore = typeof value === "string" ? value : JSON.stringify(value);
      const obj = JSON.parse(toStore);
      if (!obj || typeof obj !== "object") throw new Error("value is not an object");
      localStorage.setItem(key, toStore);
      report.imported++;
    } catch (e) {
      report.skipped.push({ key, reason: e?.message || "parse/write failed" });
    }
  }
  // labStores is OPTIONAL and absent from every backup taken before it existed.
  // Absent means "this file says nothing about those stores", so they are left
  // exactly as they are — only a file that carries the section replaces them.
  const stores = parsed.labStores;
  if (stores && typeof stores === "object" && !Array.isArray(stores)) {
    const edits = stores.playerEdits;
    if (edits && typeof edits === "object" && !Array.isArray(edits)) {
      if (Object.keys(edits).length) { savePlayerEdits(edits); report.playerEdits = Object.keys(edits).length; }
      else clearAllPlayerEdits();  // an empty store is a state worth restoring too
    }
    if (stores.depthOverrides && typeof stores.depthOverrides === "object" && !Array.isArray(stores.depthOverrides)) {
      report.depthTeams = restoreAllDepthOverrides(stores.depthOverrides);
    }
  }
  return report;
}
